import React, { ReactElement, useState, useCallback } from 'react';
import clsx from 'clsx';
import { Input, Button } from '@appsumo/dorado-react';

import { useCart } from '~/lib/cart';
import { useCheckoutAPI, useCheckoutState } from '~/hooks/checkout';
import { STEP } from '~/constants/checkout';
import { useEvent } from '~/lib/events';

import { Loader } from '~/components/ui';

interface ManualCouponProps {
  isCouponApplied: boolean;
}

export default React.memo(function ManualCoupon({
  isCouponApplied,
}: Readonly<ManualCouponProps>): ReactElement {
  const { applyCoupon } = useCart();
  const { setStepErrorMessage, refreshDiscountData } = useCheckoutAPI();
  const { stepErrorMessageObject } = useCheckoutState();
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [loading, setLoading] = useState(false);

  // Clear error if a coupon is added via the apply button
  useEvent('coupon:lastAdded', () => {
    if (couponError) setCouponError('');
  });

  const onSubmitCode = useCallback(() => {
    setLoading(true);

    // If the error from a coupon apply button is used on checkout, remove the existing error message
    if (stepErrorMessageObject[STEP.CREDITS_COUPONS]) {
      setStepErrorMessage(STEP.CREDITS_COUPONS, '');
    }

    applyCoupon(couponCode)
      .then((error: string) => {
        if (error) setCouponError(error);
        else {
          setCouponCode('');
          refreshDiscountData();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    applyCoupon,
    couponCode,
    refreshDiscountData,
    setCouponError,
    setStepErrorMessage,
    stepErrorMessageObject,
  ]);

  const labelClassName = clsx(
    'mb-2 font-header font-semibold',
    couponError ? 'text-brick' : 'text-midnight',
  );

  return (
    <div>
      <div className="flex w-full flex-col rounded border border-sundog p-4">
        <label htmlFor="manual-coupon" className={labelClassName}>
          Apply a discount code
        </label>
        <div className="flex flex-col md:flex-row">
          <div className="grow [&>div>input]:w-full">
            <Input
              inputId="manual-coupon"
              type="text"
              name="manual-coupon"
              value={couponCode}
              error={couponError}
              disabled={isCouponApplied}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (couponError) setCouponError('');
                setCouponCode(e.target.value);
              }}
            />
          </div>
          <Button
            secondary
            type="button"
            className="mt-2 inline-flex w-full justify-center self-start whitespace-nowrap md:ml-4 md:mt-0 md:w-fit"
            onClick={onSubmitCode}
            disabled={isCouponApplied || loading || !couponCode}
          >
            <span>Apply to cart</span>
            {loading && <Loader size="sm" className="ml-2" />}
          </Button>
        </div>
      </div>
    </div>
  );
});
